import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { useConfigStore } from "@/stores/widget-store";
import { Button } from "../ui/button";
import { Switch } from "../ui/switch";
import { Label } from "../ui/label";
import { Checkbox } from "../ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Slider } from "../ui/slider";
import { BrowserPreview } from "../preview/BrowserPreview";
import { ToolbarIcon } from "../preview/ToolbarIcon";
  
  interface AccessibilityToolbarTabProps {
    onSave: () => Promise<void>;
  }
  
  export const AccessibilityToolbarTab = ({
    onSave,
  }: AccessibilityToolbarTabProps) => {
    const { config, setToolbar, isLoading, error } = useConfigStore();
    const toolbar = config.toolbar;
  
    // Calculate adjusted position for preview
    // Preview container height: 220px, Icon height: 36px
    // Map user's 0-100% to actual position accounting for icon height
    // This keeps 100% from pushing icon outside the container
    const PREVIEW_HEIGHT = 220;
    const ICON_HEIGHT = 36;
    const calculatePreviewPosition = (percentage: number): number => {
      // Formula: actualPosition = (percentage / 100) * ((containerHeight - iconHeight) / containerHeight) * 100
      return (percentage / 100) * ((PREVIEW_HEIGHT - ICON_HEIGHT - (PREVIEW_HEIGHT * 0.15)) / PREVIEW_HEIGHT) * 100;
    };
  
    // Master toggle controls if fields are disabled
    const isDisabled = !toolbar.enabled;
  
    return (
      <Card
          className="cy:rounded-lg cy:shadow-none! cy:w-full cy:sm:p-8"
          style={{
            background: "#FFFFFF",
            border: "1px solid #CACEE4",
            borderRadius: "12px",
            gap: "8px",
          }}
        >
        {/* Header with Master Toggle */}
        <CardHeader className="cy:border-b cy:border-gray-200 cy:pb-6 cy:px-0">
          <div className="cy:flex cy:items-center cy:gap-4 cy:mb-2">
            <CardTitle className="cy:text-xl cy:font-semibold">
              Toolbar
            </CardTitle>
            <Switch
              checked={toolbar.enabled}
              onCheckedChange={(checked) => setToolbar("enabled", checked)}
            />
          </div>
          <CardDescription className="cy:text-sm cy:text-gray-600">
            Enable a simple display settings toolbar on your website with high
            contrast mode and text size enlarging.
          </CardDescription>
        </CardHeader>
  
        <CardContent className="cy:px-0 cy:pt-6">
          <div className="cy:space-y-8">
            {/* Features Section - 4 Checkboxes */}
            <div className="cy:space-y-4">
              <div className="cy:flex cy:items-center cy:gap-3">
                <Checkbox
                  id="fontSize"
                  checked={toolbar.features.fontSize}
                  disabled={isDisabled}
                  onCheckedChange={(checked) =>
                    setToolbar("features.fontSize", checked)
                  }
                />
                <Label
                  htmlFor="fontSize"
                  className={`cy:text-sm cy:cursor-pointer ${
                    isDisabled ? "cy:text-gray-400" : ""
                  }`}
                >
                  Include font size button
                </Label>
              </div>
  
              <div className="cy:flex cy:items-center cy:gap-3">
                <Checkbox
                  id="contrast"
                  checked={toolbar.features.contrast}
                  disabled={isDisabled}
                  onCheckedChange={(checked) =>
                    setToolbar("features.contrast", checked)
                  }
                />
                <Label
                  htmlFor="contrast"
                  className={`cy:text-sm cy:cursor-pointer ${
                    isDisabled ? "cy:text-gray-400" : ""
                  }`}
                >
                  Include contrast button
                </Label>
              </div>
  
              <div className="cy:flex cy:items-center cy:gap-3">
                <Checkbox
                  id="grayscale"
                  checked={toolbar.features.grayscale}
                  disabled={isDisabled}
                  onCheckedChange={(checked) =>
                    setToolbar("features.grayscale", checked)
                  }
                />
                <Label
                  htmlFor="grayscale"
                  className={`cy:text-sm cy:cursor-pointer ${
                    isDisabled ? "cy:text-gray-400" : ""
                  }`}
                >
                  Test with grayscale
                </Label>
              </div>
  
              <div className="cy:flex cy:items-center cy:gap-3">
                <Checkbox
                  id="hideSmall"
                  checked={toolbar.features.hideOnSmallScreens}
                  disabled={isDisabled}
                  onCheckedChange={(checked) =>
                    setToolbar("features.hideOnSmallScreens", checked)
                  }
                />
                <Label
                  htmlFor="hideSmall"
                  className={`cy:text-sm cy:cursor-pointer ${
                    isDisabled ? "cy:text-gray-400" : ""
                  }`}
                >
                  Hide toolbar on small screens.
                </Label>
              </div>
            </div>
  
            {/* Toolbar Settings Section */}
            {/* <div className="cy:pt-4 cy:border-t cy:border-gray-200">
              <h3 className="cy:text-base cy:font-semibold cy:mb-4">
                Toolbar settings
              </h3>
  
              <div className="cy:space-y-2">
                <Label
                  htmlFor="fontSizeSelect"
                  className={`cy:text-sm ${isDisabled ? "cy:text-gray-400" : ""}`}
                >
                  Toolbar font size
                </Label>
                <Select
                  value={toolbar.settings.fontSize.toString()}
                  onValueChange={(value) =>
                    setToolbar("settings.fontSize", parseInt(value))
                  }
                  disabled={isDisabled}
                >
                  <SelectTrigger id="fontSizeSelect" className="cy:w-32">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[12, 14, 16, 18, 20, 22, 24].map((size) => (
                      <SelectItem key={size} value={size.toString()}>
                        {size}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div> */}
  
            {/* Placing Toolbar Section with Preview */}
            <div className="cy:p-4 cy:border cy:border-gray-200">
              <h4 className="cy:text-base cy:font-semibold cy:pb-2">
                Placing toolbar
              </h4>
  
              <div className="cy:grid cy:grid-cols-2 cy:gap-8">
                {/* Left: Controls */}
                <div className="cy:space-y-6">
                  {/* Position Buttons */}
                  <div>
                    <Label
                      className={`cy:block cy:mb-3 cy:text-sm ${
                        isDisabled ? "cy:text-gray-400" : ""
                      }`}
                    >
                      Position
                    </Label>
                    <div className="cy:flex cy:gap-3">
                      <label
                        className={`cy:flex-1 cy:inline-flex cy:items-center cy:justify-start cy:gap-2 cy:px-3 cy:pt-2 cy:pb-1 cy:rounded cy:border-2 cy:cursor-pointer cy:transition-all ${
                          toolbar.placement.position === "left"
                            ? "cy:border-blue-600 cy:bg-white"
                            : "cy:border-gray-300 cy:bg-white"
                        } ${
                          isDisabled ? "cy:opacity-50 cy:cursor-not-allowed" : "cy:hover:border-blue-400"
                        }`}
                      >
                        <input
                          type="radio"
                          name="toolbar-position"
                          value="left"
                          checked={toolbar.placement.position === "left"}
                          onChange={() => setToolbar("placement.position", "left")}
                          disabled={isDisabled}
                          className="cy:w-4 cy:h-4 cy:text-blue-600 focus:cy:ring-blue-500 cy:flex-shrink-0"
                        />
                        <span className="cy:text-[13px] cy:font-bold cy:text-gray-700 cy:leading-[16px] cy:pb-1">Left</span>
                      </label>
                      
                      <label
                        className={`cy:flex-1 cy:inline-flex cy:items-center cy:justify-start cy:gap-2 cy:px-3 cy:pt-2 cy:pb-1 cy:rounded cy:border-2 cy:cursor-pointer cy:transition-all ${
                          toolbar.placement.position === "right"
                            ? "cy:border-blue-600 cy:bg-white"
                            : "cy:border-gray-300 cy:bg-white"
                        } ${
                          isDisabled ? "cy:opacity-50 cy:cursor-not-allowed" : "cy:hover:border-blue-400"
                        }`}
                      >
                        <input
                          type="radio"
                          name="toolbar-position"
                          value="right"
                          checked={toolbar.placement.position === "right"}
                          onChange={() => setToolbar("placement.position", "right")}
                          disabled={isDisabled}
                          className="cy:w-4 cy:h-4 cy:text-blue-600 focus:cy:ring-blue-500 cy:flex-shrink-0"
                        />
                        <span className="cy:text-[13px] cy:font-bold cy:text-gray-700 cy:leading-[16px] cy:pb-1">Right</span>
                      </label>
                    </div>
                  </div>
  
                  {/* Vertical Offset Slider */}
                  <div>
                    <Label
                      className={`cy:block cy:mb-3 cy:text-sm ${
                        isDisabled ? "cy:text-gray-400" : ""
                      }`}
                    >
                      Vertical position
                    </Label>
                    <div className="cy:flex cy:items-center cy:gap-4">
                      <Slider
                        value={[toolbar.placement.verticalOffset]}
                        onValueChange={(values) =>
                          setToolbar("placement.verticalOffset", values[0])
                        }
                        max={80}
                        min={10}
                        step={1}
                        disabled={isDisabled}
                        className="cy:flex-1"
                      />
                      <Select
                        value={toolbar.placement.verticalOffset.toString()}
                        onValueChange={(value) =>
                          setToolbar("placement.verticalOffset", parseInt(value))
                        }
                        disabled={isDisabled}
                      >
                        <SelectTrigger className="cy:w-20">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {Array.from({ length: 72 }, (_, i) => i + 9).map((val) => (
                            <SelectItem key={val} value={val.toString()}>
                              {val}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <span className="cy:text-sm cy:text-gray-600">%</span>
                    </div>
                  </div>
                </div>
  
                {/* Right: Preview */}
                <div className="cy:relative cy:flex cy:items-center cy:justify-center">
                  <div 
                    className={`cy:relative cy:w-[381px] cy:h-[220px] cy:transition-opacity cy:duration-200 ${
                      isDisabled ? "cy:opacity-40 cy:grayscale cy:pointer-events-none" : ""
                    }`}
                  >
                    {/* Browser Preview Background */}
                    <BrowserPreview />

                    {/* Toolbar Icon - Positioned dynamically */}
                    <div
                      className="cy:absolute cy:transition-all cy:duration-300"
                      style={{
                        [toolbar.placement.position]: "0px",
                        bottom: `${calculatePreviewPosition(toolbar.placement.verticalOffset)}%`,
                        transform: toolbar.placement.position === "left" ? "scaleX(-1)" : "scaleX(1)",
                      }}
                    >
                      <ToolbarIcon />
                    </div>
                  </div>
                </div>
              </div>
            </div>
  
            {/* Save Button */}
            <div className="cy:pt-4">
              {error && (
                <div className="cy:text-red-500 cy:mb-2 cy:text-sm">{error}</div>
              )}
              <Button
                onClick={onSave}
                disabled={isLoading}
                className="cy:rounded! cy:font-semibold!"
              >
                {isLoading ? "Saving..." : "Save settings"}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };